import React, { useState, useEffect, useRef } from 'react';
import './TemplatesPage.css';
import { FACEBOOK_LANGUAGES } from './constants';
import EmojiPicker from 'emoji-picker-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faReply, faExternalLinkAlt, faPhone, faClone, faBolt,
    faImage, faVideo, faFile, faShoppingCart
} from '@fortawesome/free-solid-svg-icons';
import WhatsAppPreview from './WhatsAppPreview';
import MediaUpload from './MediaUpload';

const API_URL = process.env.REACT_APP_API_URL;
const BASE_URL = `${API_URL}/whatsapp`;

const CATEGORIES = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utility' },
    { value: 'AUTHENTICATION', label: 'Authentication' }
];

const SUB_CATEGORIES = {
    'MARKETING': [
        { value: 'CUSTOM', label: 'Custom (Default)' },
        { value: 'CATALOG', label: 'Catalog' },
        { value: 'mpm', label: 'Multi-Product' },
        { value: 'spm', label: 'Single Product' },
        { value: 'flows', label: 'Flows' },
        { value: 'product_carousel', label: 'Product Carousel' },
        { value: 'media_carousel', label: 'Media Carousel' },
        { value: 'calling_permission', label: 'Calling Permission' },
        { value: 'limited_time_offer', label: 'Limited Time Offer' }
    ],
    'UTILITY': [
        { value: 'CUSTOM', label: 'Custom (Default)' },
        { value: 'ORDER_DETAILS', label: 'Order Details' },
        { value: 'ORDER_STATUS', label: 'Order Status' },
        { value: 'calling_permission', label: 'Calling Permission' }
    ],
    'AUTHENTICATION': [
        { value: 'CUSTOM', label: 'Custom (Default)' }
    ]
};

const BUTTON_TYPES = [
    { value: 'QUICK_REPLY', label: 'Quick Reply' },
    { value: 'PHONE_NUMBER', label: 'Phone Number' },
    { value: 'URL', label: 'Website URL' },
    { value: 'COPY_CODE', label: 'Copy Code' },
    { value: 'FLOW', label: 'Flow Button' }
];

const CreateTemplateModal = ({ isOpen, onClose, token, onSuccess, initialData = null }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // --- Language State ---
    const [langSearch, setLangSearch] = useState('');
    const [showLangDropdown, setShowLangDropdown] = useState(false);
    const langWrapperRef = useRef(null);

    // --- Rich Text State ---
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const textAreaRef = useRef(null);
    const emojiWrapperRef = useRef(null);

    // --- Hover State for Submit ---
    const [isHoveringSubmit, setIsHoveringSubmit] = useState(false);

    // --- Form States ---
    const [basicInfo, setBasicInfo] = useState({
        name: '',
        category: 'MARKETING',
        sub_category: 'CUSTOM',
        language: 'en'
    });

    const [header, setHeader] = useState({
        type: 'NONE', // NONE, TEXT, IMAGE, VIDEO, DOCUMENT, PRODUCT
        text: '',
        mediaFile: null,
        mediaHandle: null,
        mediaPreviewUrl: null
    });

    const [body, setBody] = useState('');
    const [footer, setFooter] = useState('');
    const [buttons, setButtons] = useState([]); // Array of button objects

    // Initialize or Reset
    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                setBasicInfo({
                    ...initialData.basicInfo,
                    name: `${initialData.basicInfo.name}_copy`,
                    // Basic fallback if sub_category missing
                    sub_category: initialData.basicInfo.sub_category || 'CUSTOM'
                });
                setHeader(initialData.header);
                setBody(initialData.body);
                setFooter(initialData.footer);
                setButtons(initialData.buttons);
            } else {
                resetForm();
            }
        }
    }, [isOpen, initialData]);

    // Close dropdowns on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (langWrapperRef.current && !langWrapperRef.current.contains(event.target)) {
                setShowLangDropdown(false);
            }
            if (emojiWrapperRef.current && !emojiWrapperRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [langWrapperRef, emojiWrapperRef]);

    const resetForm = () => {
        setBasicInfo({ name: '', category: 'MARKETING', sub_category: 'CUSTOM', language: 'en' });
        setHeader({ type: 'NONE', text: '', mediaFile: null, mediaHandle: null, mediaPreviewUrl: null });
        setBody('');
        setFooter('');
        setButtons([]);
        setError('');
        setLangSearch('');
        setMediaUploadError(null);
    };

    const [uploadingMedia, setUploadingMedia] = useState(false);
    const [mediaUploadError, setMediaUploadError] = useState(null);
    const [uploadTarget, setUploadTarget] = useState({ isCard: false, index: -1 });

    const handleMediaUpload = async (e, isCard = false, cardIndex = -1) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validation
        const MEDIA_RULES = {
            "image/jpeg": 5 * 1024 * 1024,
            "image/png": 5 * 1024 * 1024,
            "video/mp4": 16 * 1024 * 1024,
            "video/3gpp": 16 * 1024 * 1024,
            "application/pdf": 10 * 1024 * 1024,
        };

        if (!MEDIA_RULES[file.type]) {
            setMediaUploadError("Invalid file type. Allowed: JPEG, PNG, MP4, 3GPP, PDF");
            return;
        }
        if (file.size > MEDIA_RULES[file.type]) {
            setMediaUploadError(`File too large. Limit is ${MEDIA_RULES[file.type] / (1024 * 1024)}MB`);
            return;
        }

        setUploadingMedia(true);
        setMediaUploadError(null);
        setUploadTarget({ isCard, index: cardIndex });

        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${BASE_URL}/upload_template_media`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) throw new Error("Upload failed");

            const result = await response.json();
            const handle = result.h || result.id || (result.data && (result.data.h || result.data.id));
            // If the API returns a handle for templates (resumable upload), use that.
            // For now assuming the standard upload_media endpoint returns an ID used for handles.

            if (handle) {
                const previewUrl = URL.createObjectURL(file);

                if (isCard && cardIndex >= 0) {
                    setCards(prevCards => {
                        const newCards = [...prevCards];
                        newCards[cardIndex] = {
                            ...newCards[cardIndex],
                            mediaFile: file,
                            mediaHandle: handle,
                            mediaPreviewUrl: previewUrl
                        };
                        return newCards;
                    });
                } else {
                    setHeader(prev => ({
                        ...prev,
                        mediaFile: file,
                        mediaHandle: handle,
                        mediaPreviewUrl: previewUrl
                    }));
                }
            } else {
                throw new Error("No handle received");
            }
        } catch (err) {
            console.error(err);
            setMediaUploadError("Upload failed. Please try again.");
        } finally {
            setUploadingMedia(false);
        }
    };

    const handleRemoveMedia = (isCard = false, cardIndex = -1) => {
        if (isCard && cardIndex >= 0) {
            setCards(prevCards => {
                const newCards = [...prevCards];
                newCards[cardIndex] = {
                    ...newCards[cardIndex],
                    mediaFile: null,
                    mediaHandle: null,
                    mediaPreviewUrl: null
                };
                return newCards;
            });
        } else {
            setHeader(prev => ({
                ...prev,
                mediaFile: null,
                mediaHandle: null,
                mediaPreviewUrl: null
            }));
        }
        setMediaUploadError(null);
    };



    // --- Validation Rules & Component Logic ---

    // Define rules for each sub_category
    const getRules = (subCat) => {
        switch (subCat) {
            case 'CATALOG':
                return {
                    headerAllowed: false,
                    buttonsFixed: [{ type: 'CATALOG', text: 'View catalog' }]
                };
            case 'spm':
                return {
                    headerTypeFixed: 'PRODUCT',
                    buttonsFixed: [{ type: 'SPM', text: 'View' }]
                };
            case 'mpm':
                return {
                    headerTypeFixed: 'TEXT',
                    headerTextLimit: true,
                    buttonsFixed: [{ type: 'MPM', text: 'View items' }]
                };
            case 'ORDER_DETAILS':
                return {
                    headerTypeFixed: 'TEXT',
                    buttonsFixed: [{ type: 'ORDER_DETAILS', text: 'Review and Pay' }],
                    injectAttributes: { display_format: 'order_details' }
                };
            case 'ORDER_STATUS':
                return {
                    headerAllowed: false,
                    buttonsAllowed: false,
                    bodyVariablesAllowed: false,
                    injectAttributes: { sub_category: 'ORDER_STATUS' }
                };
            case 'calling_permission':
                return {
                    headerAllowed: false,
                    footerAllowed: true,
                    buttonsAllowed: false,
                    onlyBodyAllowed: false,
                    injectComponents: [{ type: 'CALL_PERMISSION_REQUEST' }]
                };
            case 'product_carousel':
                return {
                    isCarousel: true,
                    carouselType: 'PRODUCT',
                    headerAllowed: false, // No main header
                    buttonsAllowed: false, // No generic buttons
                    cardLimit: 10,
                    cardButtonsAllowed: true
                };
            case 'media_carousel':
                return {
                    isCarousel: true,
                    carouselType: 'MEDIA',
                    headerAllowed: false, // No main header
                    buttonsAllowed: false, // No generic buttons
                    cardLimit: 10,
                    cardButtonsAllowed: true
                };
            default:
                return { headerAllowed: true, buttonsAllowed: true };
        }
    };

    // State for Carousel (Cards)
    const [cards, setCards] = useState([]);
    // Global configuration for carousel buttons to enforce consistency
    const [carouselConfig, setCarouselConfig] = useState({
        buttonMode: 'DEFAULT', // 'DEFAULT' (Fixed SPM) or 'CUSTOM'
        buttonSlots: [] // Array of types e.g. ['QUICK_REPLY', 'URL']
    });

    const validationErrors = [];

    // Derived state for current rules
    const rules = getRules(basicInfo.sub_category);

    // Effect to enforce rules when sub_category changes
    useEffect(() => {
        const r = getRules(basicInfo.sub_category);

        // Reset Header based on rules
        if (r.headerAllowed === false) {
            setHeader(prev => ({ ...prev, type: 'NONE' }));
        } else if (r.headerTypeFixed) {
            setHeader(prev => ({ ...prev, type: r.headerTypeFixed }));
        }

        // Reset Buttons or Set Fixed
        if (r.buttonsFixed) {
            // We don't really set 'buttons' state for fixed buttons that shouldn't be edited
            // But for preview we might want a visual representation
            // Let's use a separate logic for previewing fixed buttons
            setButtons([]);
        } else if (r.buttonsAllowed === false) {
            setButtons([]);
        }

    }, [basicInfo.sub_category]);

    // Enforce Product Carousel Default Mode
    useEffect(() => {
        if (basicInfo.sub_category === 'product_carousel') {
            setCarouselConfig(prev => ({ ...prev, buttonMode: 'DEFAULT', buttonSlots: [] }));
        } else if (basicInfo.sub_category === 'media_carousel') {
            setCarouselConfig(prev => ({ ...prev, buttonMode: 'CUSTOM', buttonSlots: [] }));
        }
    }, [basicInfo.sub_category]);

    // Validation Logic
    const validate = () => {
        const errors = [];
        if (!basicInfo.name) errors.push("Template Name is required.");
        if (rules.bodyAllowed !== false && !body) errors.push("Body text is required.");

        // Header Validations
        if (header.type !== 'NONE' && header.type !== 'TEXT' && header.type !== 'PRODUCT' && !header.mediaHandle) {
            if (header.mediaFile) errors.push("Header media is still uploading.");
            else if (rules.headerAllowed !== false) errors.push("Header media is required.");
        }
        if (header.type === 'TEXT' && !header.text) errors.push("Header text is required.");

        // Carousel Validations
        if (rules.isCarousel) {
            if (cards.length === 0) errors.push("At least one carousel card is required.");
            cards.forEach((card, idx) => {
                if (rules.carouselType === 'MEDIA') {
                    if (!card.mediaHandle) errors.push(`Card ${idx + 1}: Media is required (and must finish uploading).`);
                }
                // Validate Buttons
                if (carouselConfig.buttonMode === 'CUSTOM') {
                    card.buttons.forEach((btn, bIdx) => {
                        const type = carouselConfig.buttonSlots[bIdx];
                        if (!btn.text) errors.push(`Card ${idx + 1} Button ${bIdx + 1}: Text is required.`);
                        if (type === 'URL' && !btn.url && !btn.baseUrl) errors.push(`Card ${idx + 1} Button ${bIdx + 1}: URL is required.`);
                        if (type === 'PHONE_NUMBER' && !btn.phone_number) errors.push(`Card ${idx + 1} Button ${bIdx + 1}: Phone number is required.`);
                    });
                }
            });
        }

        return errors;
    };

    const activeErrors = validate();

    // --- Handlers ---

    const insertTextAtCursor = (text) => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText = body.substring(0, start) + text + body.substring(end);
        setBody(newText);

        // Restore focus and cursor
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + text.length, start + text.length);
        }, 0);
    };

    const handleEmojiClick = (emojiObj) => {
        insertTextAtCursor(emojiObj.emoji);
        setShowEmojiPicker(false);
    };

    const wrapSelectedText = (char) => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = body.substring(start, end);

        // If nothing selected, just insert marker? Or wrap empty?
        // Let's just wrap
        const newText = body.substring(0, start) + char + selected + char + body.substring(end);
        setBody(newText);

        setTimeout(() => {
            textarea.focus();
            // Move cursor to end of wrap
            textarea.setSelectionRange(end + 2, end + 2);
        }, 0);
    }

    const traverseAndCreateSamples = (text) => {
        const regex = /\{\{(\d+)\}\}/g;
        const matches = [...text.matchAll(regex)];
        if (matches.length > 0) {
            // Unique var count calculation to be safe or just length
            // FB expects 1 sample per unique variable.
            // Simplified: just return a string for each match.
            return {
                body_text: [matches.map((_, i) => `sample_${i + 1}`)]
            };
        }
        return null;
    }

    const handleSubmit = async () => {
        // --- 1. Basic Validation ---
        if (!basicInfo.name || !body) {
            setError('Template name and body text are required.');
            return;
        }
        if (header.mediaFile && !header.mediaHandle) {
            setError('Media is still uploading. Please wait.');
            return;
        }

        setLoading(true);
        setError('');

        const components = [];

        // --- 2. Injected Only Components (Ordering matters for strict types) ---
        if (rules.injectComponents) {
            components.push(...rules.injectComponents);
        }

        // --- 3. CAROUSEL HANDLING ---
        if (rules.isCarousel) {
            // BODY is mandatory for Carousel wrapper
            const bodyComponent = { type: 'BODY', text: body };
            const bodySamples = traverseAndCreateSamples(body);
            if (bodySamples) bodyComponent.example = bodySamples;
            components.push(bodyComponent);

            // CAROUSEL Component
            const cardsPayload = cards.map(card => {
                const cardComps = [];

                // Card Header
                if (rules.carouselType === 'PRODUCT') {
                    cardComps.push({ type: 'HEADER', format: 'PRODUCT' });
                } else if (rules.carouselType === 'MEDIA') {
                    const h = { type: 'HEADER', format: card.headerType };
                    // Add examples/handles for media
                    if (['IMAGE', 'VIDEO'].includes(card.headerType) && card.mediaHandle) {
                        h.example = { header_handle: [card.mediaHandle] };
                    }
                    cardComps.push(h);
                }

                // Card Body
                if (card.bodyText) {
                    cardComps.push({ type: 'BODY', text: card.bodyText });
                }

                // Card Buttons
                // If Product Carousel + Default Mode -> Send fixed SPM button
                if (rules.carouselType === 'PRODUCT' && carouselConfig.buttonMode === 'DEFAULT') {
                    cardComps.push({
                        type: 'BUTTONS',
                        buttons: [{ type: 'spm', text: 'View' }]
                    });
                }
                // Custom Buttons (Media or Product Custom)
                else if (carouselConfig.buttonMode === 'CUSTOM' && carouselConfig.buttonSlots.length > 0) {
                    const cardBtns = {
                        type: 'BUTTONS',
                        buttons: carouselConfig.buttonSlots.map((slotType, bIdx) => {
                            const btnData = card.buttons[bIdx] || {};
                            // Construct button payload based on slot type + card data
                            if (slotType === 'QUICK_REPLY') return { type: 'QUICK_REPLY', text: btnData.text };
                            if (slotType === 'PHONE_NUMBER') return { type: 'PHONE_NUMBER', text: btnData.text, phone_number: btnData.phone_number };
                            if (slotType === 'URL') {
                                const urlB = { type: 'URL', text: btnData.text };
                                if (btnData.urlType === 'DYNAMIC') {
                                    urlB.url = `${btnData.baseUrl}{{1}}`;
                                    urlB.example = [btnData.exampleUrlSuffix || "sample"];
                                } else {
                                    urlB.url = btnData.url;
                                }
                                return urlB;
                            }
                            return null;
                        }).filter(Boolean)
                    };
                    cardComps.push(cardBtns);
                }

                return { components: cardComps };
            });

            components.push({ type: 'CAROUSEL', cards: cardsPayload });

        } else if (!rules.onlyBodyAllowed && !rules.injectComponents) {
            // --- 4. STANDARD TEMPLATE (Not injected, Not Carousel) ---

            // Header (if allowed)
            if (rules.headerAllowed !== false && header.type !== 'NONE') {
                const headerComponent = { type: 'HEADER', format: header.type };
                if (header.type === 'TEXT') {
                    headerComponent.text = header.text;
                    if (header.text.includes('{{')) headerComponent.example = { header_text: ["sample"] };
                } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type)) {
                    if (header.mediaHandle) headerComponent.example = { header_handle: [header.mediaHandle] };
                } else if (header.type === 'PRODUCT') {
                    // Just type/format needed
                }
                components.push(headerComponent);
            }

            // Body
            const bodyComponent = { type: 'BODY', text: body };
            const bodySamples = traverseAndCreateSamples(body);
            if (bodySamples) bodyComponent.example = bodySamples;

            // Strict check: Order Status = Variables NOT allowed
            if (rules.bodyVariablesAllowed === false && bodySamples) {
                setError('Variables ({{1}}, {{2}}) are not allowed in Body for this category.');
                setLoading(false);
                return;
            }
            components.push(bodyComponent);

            // Footer
            if ((rules.footerAllowed !== false) && footer) { // Default true
                components.push({ type: 'FOOTER', text: footer });
            }

            // Buttons (Include Fixed and Dynamic)
            let templateButtons = [];

            // 1. Add Fixed Buttons (if any)
            if (rules.buttonsFixed) {
                templateButtons = [...rules.buttonsFixed];
            }

            // 2. Add Dynamic Buttons
            if (rules.buttonsAllowed !== false && buttons.length > 0) {
                const dynamicButtons = buttons.map(btn => {
                    const b = { type: btn.type, text: btn.text };
                    if (btn.type === 'PHONE_NUMBER') b.phone_number = btn.phone_number;
                    if (btn.type === 'URL') {
                        if (btn.urlType === 'DYNAMIC') {
                            b.url = `${btn.baseUrl}{{1}}`;
                            b.example = [btn.exampleUrlSuffix || "sample"];
                        } else {
                            b.url = btn.url;
                        }
                    }
                    if (btn.type === 'COPY_CODE') b.example = "CODE123";
                    if (btn.type === 'FLOW') {
                        b.flow_id = btn.flow_id;
                        b.flow_action = btn.flow_action || 'navigate';
                        b.navigate_screen = btn.navigate_screen || 'INIT';
                    }
                    return b;
                });
                templateButtons = [...templateButtons, ...dynamicButtons];
            }

            if (templateButtons.length > 0) {
                components.push({ type: 'BUTTONS', buttons: templateButtons });
            }
        }

        // Handling Calling Permission Special Case
        if (rules.injectComponents && components.length === 1 && components[0].type === 'CALL_PERMISSION_REQUEST') {
            if (body) {
                const realComponents = [];
                const b = { type: 'BODY', text: body };
                if (traverseAndCreateSamples(body)) {
                    b.example = traverseAndCreateSamples(body);
                }
                realComponents.push(b);
                if (footer) realComponents.push({ type: 'FOOTER', text: footer });
                realComponents.push(...rules.injectComponents);

                components.length = 0;
                components.push(...realComponents);
            }
        }


        const payload = {
            name: basicInfo.name,
            category: basicInfo.category,
            language: basicInfo.language,
            components: components
        };

        if (basicInfo.sub_category && basicInfo.sub_category === 'ORDER_STATUS') {
            payload.sub_category = basicInfo.sub_category;
        }

        // Inject Attributes
        if (rules.injectAttributes) {
            Object.assign(payload, rules.injectAttributes);
        }

        payload.allow_category_change = true;

        try {
            const response = await fetch(`${BASE_URL}/create_template`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (response.ok) {
                onSuccess();
                onClose();
            } else {
                setError(result.message || JSON.stringify(result) || 'Failed to create template');
            }
        } catch (err) {
            setError(err.message || 'Network error.');
        } finally {
            setLoading(false);
        }
    };

    // --- Helpers for Button Management ---
    const addButton = () => setButtons([...buttons, { type: 'QUICK_REPLY', text: '' }]);
    const updateButton = (index, field, value) => {
        const newButtons = [...buttons];
        newButtons[index][field] = value;
        setButtons(newButtons);
    };
    const removeButton = (index) => setButtons(buttons.filter((_, i) => i !== index));

    // --- Language Helpers ---
    const getFilteredLanguages = () => {
        if (!langSearch) return FACEBOOK_LANGUAGES;
        return FACEBOOK_LANGUAGES.filter(l =>
            l.label.toLowerCase().includes(langSearch.toLowerCase()) ||
            l.value.toLowerCase().includes(langSearch.toLowerCase())
        );
    };

    if (!isOpen) return null;

    // Determine currently selected language label
    const currentLangLabel = FACEBOOK_LANGUAGES.find(l => l.value === basicInfo.language)?.label || basicInfo.language;

    return (
        <div className="wt-modal-overlay">
            <div className="wt-modal-content">
                {/* 1. HEADER */}
                <div className="wt-modal-header">
                    <div className="wt-modal-title">Create Template</div>
                    <button className="wt-close-btn" onClick={onClose}>×</button>
                </div>

                <div className="wt-modal-body-wrapper">
                    <div className="wt-modal-left-panel">
                        <div className="wt-form-scroll-area">
                            {error && <div style={{ color: 'red', marginBottom: '16px', padding: '10px', background: '#fee2e2', borderRadius: '8px' }}>{error}</div>}

                            {/* 1. Basic Information */}
                            <div className="wt-form-section">
                                <div className="wt-section-header">Basic Information</div>
                                <div className="wt-form-group">
                                    <label className="wt-label">Template Name</label>
                                    <input
                                        className="wt-input"
                                        value={basicInfo.name}
                                        onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value.toLowerCase().replace(/ /g, '_') })}
                                        placeholder="e.g. xmas_promo_2025"
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                    <div className="wt-form-group" style={{ flex: 1, minWidth: '150px' }}>
                                        <label className="wt-label">Category</label>
                                        <select className="wt-select" value={basicInfo.category} onChange={(e) => setBasicInfo({ ...basicInfo, category: e.target.value, sub_category: 'CUSTOM' })}>
                                            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="wt-form-group" style={{ flex: 1, minWidth: '150px' }}>
                                        <label className="wt-label">Sub Category</label>
                                        <select className="wt-select" value={basicInfo.sub_category} onChange={(e) => setBasicInfo({ ...basicInfo, sub_category: e.target.value })}>
                                            {(SUB_CATEGORIES[basicInfo.category] || []).map(sc => <option key={sc.value} value={sc.value}>{sc.label}</option>)}
                                        </select>
                                    </div>
                                    <div className="wt-form-group" style={{ flex: 1, minWidth: '150px', marginTop: '6px' }} ref={langWrapperRef}>
                                        <label className="wt-label">Language</label>
                                        <div className="wt-search-dropdown">
                                            <input
                                                className="wt-input"
                                                value={showLangDropdown ? langSearch : currentLangLabel}
                                                onChange={(e) => setLangSearch(e.target.value)}
                                                onFocus={() => { setShowLangDropdown(true); setLangSearch(''); }}
                                                placeholder="Search Language..."
                                            />
                                            {showLangDropdown && (
                                                <div className="wt-search-results">
                                                    {getFilteredLanguages().map(l => (
                                                        <div
                                                            key={l.value}
                                                            className="wt-search-item"
                                                            onClick={() => {
                                                                setBasicInfo({ ...basicInfo, language: l.value });
                                                                setShowLangDropdown(false);
                                                            }}
                                                        >
                                                            {l.label}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Header */}
                            {rules.headerAllowed !== false && (
                                <div className="wt-form-section">
                                    <div className="wt-section-header">Header (Optional)</div>
                                    <div className="wt-form-group">
                                        <label className="wt-label">Header Type</label>
                                        <select
                                            className="wt-select"
                                            value={header.type}
                                            onChange={(e) => setHeader({ ...header, type: e.target.value })}
                                            disabled={!!rules.headerTypeFixed}
                                        >
                                            {/* Only show PRODUCT if Marketing and explicitly fixed (e.g. SPM) */}
                                            <option value="NONE">None</option>
                                            <option value="TEXT">Text</option>
                                            <option value="IMAGE">Image</option>
                                            <option value="VIDEO">Video</option>
                                            <option value="DOCUMENT">Document</option>
                                            {rules.headerTypeFixed === 'PRODUCT' && <option value="PRODUCT">Product</option>}
                                        </select>
                                    </div>
                                    {header.type === 'TEXT' && (
                                        <div className="wt-form-group">
                                            <label className="wt-label">Header Text</label>
                                            <input className="wt-input" value={header.text} onChange={(e) => setHeader({ ...header, text: e.target.value })} />
                                        </div>
                                    )}
                                    {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header.type) && (
                                        <div className="wt-form-group">
                                            <label className="wt-label">Header Media</label>
                                            <MediaUpload
                                                type={header.type}
                                                onUpload={handleMediaUpload}
                                                onRemove={handleRemoveMedia}
                                                previewUrl={header.mediaPreviewUrl}
                                                fileName={header.mediaFile ? header.mediaFile.name : null}
                                                isUploaded={!!header.mediaHandle}
                                                uploading={uploadingMedia}
                                                error={mediaUploadError}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* 3. Body with Rich Text */}
                            <div className="wt-form-section">
                                <div className="wt-section-header">Body (Required)</div>
                                <div className="wt-form-group">
                                    <label className="wt-label">Message Text</label>
                                    <div className="wt-rich-text-toolbar">
                                        <button className="wt-toolbar-btn" onClick={() => wrapSelectedText('*')} title="Bold">B</button>
                                        <button className="wt-toolbar-btn" onClick={() => wrapSelectedText('_')} title="Italic">I</button>
                                        <button className="wt-toolbar-btn" style={{ textDecoration: 'line-through' }} onClick={() => wrapSelectedText('~')} title="Strikethrough">S</button>
                                        <div style={{ position: 'relative' }} ref={emojiWrapperRef}>
                                            <button className="wt-toolbar-btn" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>😀</button>
                                            {showEmojiPicker && (
                                                <div className="emoji-picker-overlay">
                                                    <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <textarea
                                        ref={textAreaRef}
                                        className="wt-textarea wt-textarea-rich"
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder="Hello {{1}}, check out our new offers..."
                                    />
                                    <div className="wt-helper-text">Values: &#123;&#123;1&#125;&#125; Format: *Bold* _Italic_ ~Strike~</div>
                                </div>
                            </div>

                            {/* CAROUSEL CARDS */}
                            {rules.isCarousel && (
                                <div className="wt-form-section">
                                    <div className="wt-section-header">
                                        Carousel Cards ({cards.length}/{rules.cardLimit})
                                        {/* Global Carousel Button Config UI */}
                                        {rules.carouselType === 'PRODUCT' && (
                                            <div style={{ marginBottom: '10px', display: 'flex', gap: '16px' }}>
                                                <label><input type="radio" checked={carouselConfig.buttonMode === 'DEFAULT'} onChange={() => setCarouselConfig({ ...carouselConfig, buttonMode: 'DEFAULT', buttonSlots: [] })} /> Use View Button (Fixed)</label>
                                                <label><input type="radio" checked={carouselConfig.buttonMode === 'CUSTOM'} onChange={() => setCarouselConfig({ ...carouselConfig, buttonMode: 'CUSTOM' })} /> Use Custom Buttons</label>
                                            </div>
                                        )}

                                        {carouselConfig.buttonMode === 'CUSTOM' && (
                                            <div style={{ background: '#e0f2f1', padding: '8px', borderRadius: '6px', marginBottom: '10px', fontSize: '13px' }}>
                                                <strong>Card Button Structure (Applies to ALL cards)</strong>
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                                                    {carouselConfig.buttonSlots.map((slot, sIdx) => (
                                                        <div key={sIdx} style={{ background: 'white', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            {slot} <button onClick={() => {
                                                                const newSlots = carouselConfig.buttonSlots.filter((_, i) => i !== sIdx);
                                                                setCarouselConfig({ ...carouselConfig, buttonSlots: newSlots });
                                                            }} style={{ color: 'red', border: 'none', background: 'none', cursor: 'pointer' }}>×</button>
                                                        </div>
                                                    ))}
                                                    {carouselConfig.buttonSlots.length < 2 && (
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button className="wt-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => setCarouselConfig({ ...carouselConfig, buttonSlots: [...carouselConfig.buttonSlots, 'QUICK_REPLY'] })}>+ Quick Reply</button>
                                                            <button className="wt-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => setCarouselConfig({ ...carouselConfig, buttonSlots: [...carouselConfig.buttonSlots, 'URL'] })}>+ URL</button>
                                                            <button className="wt-btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => setCarouselConfig({ ...carouselConfig, buttonSlots: [...carouselConfig.buttonSlots, 'PHONE_NUMBER'] })}>+ Phone</button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        <button className="wt-add-btn" onClick={() => {
                                            if (cards.length < (rules.cardLimit || 10)) {
                                                setCards([...cards, { headerType: rules.carouselType === 'MEDIA' ? 'IMAGE' : 'PRODUCT', bodyText: '', buttons: [] }]);
                                            }
                                        }}>+ Add Card</button>
                                    </div>
                                    {cards.map((card, idx) => (
                                        <div key={idx} className="wt-carousel-card-container">
                                            <div className="wt-carousel-card-header">
                                                <strong>Card {idx + 1}</strong>
                                                <button onClick={() => setCards(cards.filter((_, i) => i !== idx))} style={{ color: '#EF4444', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 500 }}>Delete Card</button>
                                            </div>

                                            {/* Card Header */}
                                            {rules.carouselType === 'MEDIA' && (
                                                <div className="wt-form-group">
                                                    <label className="wt-label">Header Media</label>
                                                    <select className="wt-select" value={card.headerType} onChange={(e) => {
                                                        const newCards = [...cards];
                                                        newCards[idx].headerType = e.target.value;
                                                        newCards[idx].mediaFile = null;
                                                        newCards[idx].mediaHandle = null;
                                                        newCards[idx].mediaPreviewUrl = null;
                                                        setCards(newCards);
                                                    }} style={{ marginBottom: '10px' }}>
                                                        <option value="IMAGE">Image</option>
                                                        <option value="VIDEO">Video</option>
                                                    </select>

                                                    <MediaUpload
                                                        type={card.headerType}
                                                        onUpload={(e) => handleMediaUpload(e, true, idx)}
                                                        onRemove={() => handleRemoveMedia(true, idx)}
                                                        previewUrl={card.mediaPreviewUrl}
                                                        isUploaded={!!card.mediaHandle}
                                                        fileName={card.mediaFile ? card.mediaFile.name : null}
                                                        uploading={uploadingMedia && uploadTarget.isCard && uploadTarget.index === idx}
                                                        error={mediaUploadError && uploadTarget.isCard && uploadTarget.index === idx ? mediaUploadError : null}
                                                    />
                                                </div>
                                            )}
                                            {rules.carouselType === 'PRODUCT' && <div style={{ fontSize: '12px', color: '#666' }}>Header: Product Image (Dynamic)</div>}



                                            {/* Card Buttons */}
                                            {/* Card Body logic restored, only for Media Carousel */}
                                            {rules.carouselType === 'MEDIA' && (
                                                <div className="wt-form-group">
                                                    <input
                                                        className="wt-input"
                                                        placeholder="Card Body Text"
                                                        value={card.bodyText || ''}
                                                        onChange={(e) => {
                                                            const newCards = [...cards];
                                                            newCards[idx].bodyText = e.target.value;
                                                            setCards(newCards);
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Card Buttons: Rendered based on Global Config */}
                                            <div className="wt-form-group">
                                                <label className="wt-label">Buttons</label>

                                                {/* If Product Carousel Default Mode */}
                                                {rules.carouselType === 'PRODUCT' && carouselConfig.buttonMode === 'DEFAULT' && (
                                                    <div style={{ background: '#e9ecef', padding: '10px', borderRadius: '8px', opacity: 0.7 }}>
                                                        <div style={{ fontSize: '12px', fontStyle: 'italic' }}>Fixed Button</div>
                                                        <input className="wt-input" value="View Product" disabled />
                                                    </div>
                                                )}

                                                {/* Custom Mode Inputs */}
                                                {carouselConfig.buttonMode === 'CUSTOM' && carouselConfig.buttonSlots.map((slotType, bIdx) => {
                                                    const myBtn = card.buttons[bIdx] || { type: slotType, text: '' };

                                                    return (
                                                        <div key={bIdx} style={{ background: 'white', padding: '10px', borderRadius: '8px', marginBottom: '8px', border: '1px solid #e0e0e0' }}>
                                                            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '4px', color: '#555' }}>
                                                                Button {bIdx + 1}: {slotType}
                                                            </div>

                                                            <input
                                                                className="wt-input"
                                                                placeholder="Button Text"
                                                                value={myBtn.text}
                                                                onChange={(e) => {
                                                                    const newCards = [...cards];
                                                                    if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                    newCards[idx].buttons[bIdx].text = e.target.value;
                                                                    setCards(newCards);
                                                                }}
                                                                style={{ marginBottom: '8px' }}
                                                            />

                                                            {slotType === 'PHONE_NUMBER' && (
                                                                <input className="wt-input" placeholder="+91..." value={myBtn.phone_number || ''}
                                                                    onChange={(e) => {
                                                                        const newCards = [...cards];
                                                                        if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                        newCards[idx].buttons[bIdx].phone_number = e.target.value;
                                                                        setCards(newCards);
                                                                    }}
                                                                />
                                                            )}

                                                            {slotType === 'URL' && (
                                                                <div style={{ marginTop: '8px' }}>
                                                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', fontSize: '13px' }}>
                                                                        <label><input type="radio" checked={myBtn.urlType !== 'DYNAMIC'} onChange={() => {
                                                                            const newCards = [...cards];
                                                                            if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                            newCards[idx].buttons[bIdx].urlType = 'STATIC';
                                                                            setCards(newCards);
                                                                        }} /> Static</label>
                                                                        <label><input type="radio" checked={myBtn.urlType === 'DYNAMIC'} onChange={() => {
                                                                            const newCards = [...cards];
                                                                            if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                            newCards[idx].buttons[bIdx].urlType = 'DYNAMIC';
                                                                            setCards(newCards);
                                                                        }} /> Dynamic</label>
                                                                    </div>

                                                                    {myBtn.urlType === 'DYNAMIC' ? (
                                                                        <>
                                                                            <input className="wt-input" placeholder="Base URL" value={myBtn.baseUrl || ''}
                                                                                onChange={(e) => {
                                                                                    const newCards = [...cards];
                                                                                    if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                                    newCards[idx].buttons[bIdx].baseUrl = e.target.value;
                                                                                    setCards(newCards);
                                                                                }} style={{ marginBottom: '4px' }} />
                                                                            <input className="wt-input" placeholder="Sample Suffix" value={myBtn.exampleUrlSuffix || ''}
                                                                                onChange={(e) => {
                                                                                    const newCards = [...cards];
                                                                                    if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                                    newCards[idx].buttons[bIdx].exampleUrlSuffix = e.target.value;
                                                                                    setCards(newCards);
                                                                                }} />
                                                                        </>
                                                                    ) : (
                                                                        <input className="wt-input" placeholder="https://..." value={myBtn.url || ''}
                                                                            onChange={(e) => {
                                                                                const newCards = [...cards];
                                                                                if (!newCards[idx].buttons[bIdx]) newCards[idx].buttons[bIdx] = { type: slotType };
                                                                                newCards[idx].buttons[bIdx].url = e.target.value;
                                                                                setCards(newCards);
                                                                            }} />
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* 4. Footer */}
                            {rules.footerAllowed !== false && (
                                <div className="wt-form-section">
                                    <div className="wt-section-header">Footer (Optional)</div>
                                    <div className="wt-form-group">
                                        <input className="wt-input" value={footer} onChange={(e) => setFooter(e.target.value)} placeholder="e.g. Reply STOP to unsubscribe" />
                                    </div>
                                </div>
                            )}

                            {/* 5. Buttons */}
                            {rules.buttonsAllowed !== false && (
                                <div className="wt-form-section">
                                    <div className="wt-section-header">
                                        Buttons {rules.buttonsFixed ? '(Fixed via Template Type)' : ''}
                                        {/* {!rules.buttonsFixed && <button className="wt-add-btn" onClick={addButton}>+ Add Button</button>} Removed in favor of grid */}
                                    </div>
                                    {/* Fixed Buttons Display */}
                                    {rules.buttonsFixed && rules.buttonsFixed.map((btn, idx) => (
                                        <div key={`fixed-${idx}`} style={{ background: '#e9ecef', padding: '12px', borderRadius: '8px', marginBottom: '12px', opacity: 0.8 }}>
                                            <div style={{ fontStyle: 'italic', fontSize: '12px', marginBottom: '4px' }}>Fixed Button</div>
                                            <div className="wt-form-group">
                                                <input className="wt-input" value={btn.text} disabled />
                                            </div>
                                        </div>
                                    ))}

                                    {/* Button Selection Row (Premium UI) */}
                                    {!rules.buttonsFixed && (
                                        <div className="wt-button-types-row">
                                            {BUTTON_TYPES.map(bt => (
                                                <div key={bt.value} className="wt-button-type-chip" onClick={() => setButtons([...buttons, { type: bt.value, text: '' }])}>
                                                    <span className="icon">
                                                        {bt.value === 'QUICK_REPLY' && <FontAwesomeIcon icon={faReply} />}
                                                        {bt.value === 'URL' && <FontAwesomeIcon icon={faExternalLinkAlt} />}
                                                        {bt.value === 'PHONE_NUMBER' && <FontAwesomeIcon icon={faPhone} />}
                                                        {bt.value === 'COPY_CODE' && <FontAwesomeIcon icon={faClone} />}
                                                        {bt.value === 'FLOW' && <FontAwesomeIcon icon={faBolt} />}
                                                    </span>
                                                    <span className="label">{bt.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Configure Added Buttons */}
                                    {buttons.map((btn, idx) => (
                                        <div key={idx} className="wt-configured-button">
                                            <div className="wt-btn-card-header">
                                                <span className="wt-btn-card-type">{btn.type.replace('_', ' ')}</span>
                                                <div className="wt-btn-card-actions">
                                                    <button className="wt-btn-action delete" onClick={() => removeButton(idx)} title="Remove">🗑️</button>
                                                </div>
                                            </div>

                                            <div className="wt-form-group" style={{ marginBottom: '8px' }}>
                                                <input
                                                    className="wt-input"
                                                    placeholder="Button Text"
                                                    value={btn.text}
                                                    onChange={(e) => updateButton(idx, 'text', e.target.value)}
                                                />
                                            </div>

                                            {btn.type === 'PHONE_NUMBER' && (
                                                <div className="wt-form-group" style={{ marginBottom: '0' }}>
                                                    <input className="wt-input" placeholder="+91..." value={btn.phone_number || ''} onChange={(e) => updateButton(idx, 'phone_number', e.target.value)} />
                                                </div>
                                            )}

                                            {btn.type === 'URL' && (
                                                <div style={{ marginTop: '8px', background: '#F3F4F6', padding: '10px', borderRadius: '8px' }}>
                                                    <div style={{ display: 'flex', gap: '16px', marginBottom: '8px', fontSize: '13px' }}>
                                                        <label style={{ cursor: 'pointer' }}><input type="radio" checked={btn.urlType !== 'DYNAMIC'} onChange={() => updateButton(idx, 'urlType', 'STATIC')} /> Static URL</label>
                                                        <label style={{ cursor: 'pointer' }}><input type="radio" checked={btn.urlType === 'DYNAMIC'} onChange={() => updateButton(idx, 'urlType', 'DYNAMIC')} /> Dynamic URL</label>
                                                    </div>
                                                    {btn.urlType === 'DYNAMIC' ? (
                                                        <>
                                                            <input className="wt-input" placeholder="Base URL (e.g. https://site.com/)" value={btn.baseUrl || ''} onChange={(e) => updateButton(idx, 'baseUrl', e.target.value)} style={{ marginBottom: '4px' }} />
                                                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>Appends <strong>{`{{1}}`}</strong> to end</div>
                                                            <input className="wt-input" placeholder="Example Suffix (e.g. 123)" value={btn.exampleUrlSuffix || ''} onChange={(e) => updateButton(idx, 'exampleUrlSuffix', e.target.value)} />
                                                        </>
                                                    ) : (
                                                        <input className="wt-input" placeholder="https://example.com" value={btn.url || ''} onChange={(e) => updateButton(idx, 'url', e.target.value)} />
                                                    )}
                                                </div>
                                            )}

                                            {btn.type === 'FLOW' && (
                                                <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                    <input className="wt-input" placeholder="Flow ID" value={btn.flow_id || ''} onChange={(e) => updateButton(idx, 'flow_id', e.target.value)} />
                                                    <input className="wt-input" placeholder="Screen ID (INIT)" value={btn.navigate_screen || ''} onChange={(e) => updateButton(idx, 'navigate_screen', e.target.value)} />
                                                </div>
                                            )}

                                            {btn.type === 'COPY_CODE' && (
                                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                                                    Copies the code to user's clipboard.
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Mobile Preview (visible only on small screens) */}
                            <div className="wt-mobile-preview-container">
                                <div style={{ fontWeight: 600, marginBottom: 12, color: '#374151' }}>Preview</div>
                                <WhatsAppPreview
                                    header={{
                                        type: header.type,
                                        text: header.text,
                                        mediaPreviewUrl: header.mediaPreviewUrl,
                                        mediaFile: header.mediaFile
                                    }}
                                    body={body}
                                    footer={footer}
                                    buttons={(rules.buttonsFixed || []).concat(buttons)}
                                    carousel={rules.isCarousel ? {
                                        type: rules.carouselType,
                                        cards: cards,
                                        buttonMode: carouselConfig.buttonMode
                                    } : null}
                                />
                            </div>

                        </div>




                    </div> {/* Close Left Panel */}

                    {/* Right Side: Preview */}
                    <div className="wt-modal-preview">
                        <WhatsAppPreview
                            header={{
                                type: header.type,
                                text: header.text,
                                mediaPreviewUrl: header.mediaPreviewUrl,
                                mediaFile: header.mediaFile
                            }}
                            body={body}
                            footer={footer}
                            buttons={(rules.buttonsFixed || []).concat(buttons)}
                            carousel={rules.isCarousel ? {
                                type: rules.carouselType,
                                cards: cards,
                                buttonMode: carouselConfig.buttonMode
                            } : null}
                        />
                    </div>
                </div> {/* Close Body Wrapper */}

                {/* 3. FOOTER */}
                <div className="wt-buttons-area">
                    <button className="wt-btn wt-btn-secondary" onClick={onClose}>Cancel</button>
                    <div style={{ position: 'relative', display: 'inline-block' }}
                        onMouseEnter={() => setIsHoveringSubmit(true)}
                        onMouseLeave={() => setIsHoveringSubmit(false)}
                    >
                        <button
                            className="wt-btn wt-btn-primary"
                            onClick={handleSubmit}
                            disabled={loading || activeErrors.length > 0}
                            style={{ opacity: (loading || activeErrors.length > 0) ? 0.6 : 1, cursor: (loading || activeErrors.length > 0) ? 'not-allowed' : 'pointer' }}
                        >
                            {loading ? 'Submitting...' : 'Submit Template'}
                        </button>
                    </div>
                </div>

                {activeErrors.length > 0 && isHoveringSubmit && (
                    <div style={{
                        position: 'absolute',
                        bottom: '80px',
                        left: '20px',
                        background: '#333',
                        color: 'white',
                        padding: '10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        zIndex: 1000,
                        listStyleType: 'none',
                        maxWidth: '300px'
                    }}>
                        <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Missing Requirements:</div>
                        {activeErrors.slice(0, 5).map((e, i) => <div key={i}>• {e}</div>)}
                        {activeErrors.length > 5 && <div>...and {activeErrors.length - 5} more</div>}
                    </div>
                )}
            </div>
        </div >
    );
};

export default CreateTemplateModal;
